/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any, @typescript-eslint/no-misused-promises */

import { useEffect, useMemo, useState } from 'react';
import { getDateFormatted, getParquetUrl, ET } from '../../util';
import { dayjs } from '../../util/shared/date';
import useToast from '../../hooks/useToast';
import { useLog } from '../../hooks';
import { IntradayStats } from 'types';
import { DefaultService } from 'gen/stream-api/requests/services.gen';

type useStatsProps = {
  intradayDate: dayjs.Dayjs;
  intradaySym: string;
  userTraceV2Enabled: boolean;
};

export const useStats = ({
  intradayDate,
  intradaySym,
  userTraceV2Enabled,
}: useStatsProps): { statsDataMap: Map<string, IntradayStats> } => {
  // use a map for stats so that we dont fetch stats for the same date more than once
  const [statsDataMap, setStatsDataMap] = useState(new Map<string, any>());

  const { openToast } = useToast();
  const { fetchAPIWithLog, nonProdDebugLog } = useLog('useStats');

  useEffect(() => {
    // Wipe any stats associated with the old state
    setStatsDataMap(new Map());
  }, [userTraceV2Enabled]);

  const statsParquetUrl = useMemo(() => {
    // Dates before 2024-08-30 don't have 90 days of data, so just use min/max/mean
    const date = dayjs.max(dayjs.tz('2024-09-11', ET), intradayDate)!;
    return getParquetUrl('intradayStats', date, intradaySym);
  }, [intradayDate, intradaySym]);

  const fetchJson = async () => {
    if (userTraceV2Enabled) {
      return (await DefaultService.getV2OpenInterestIntradayStats({
        date: intradayDate.format('YYYY-MM-DD'),
        sym: intradaySym,
      })) as IntradayStats;
    }
    const resp = await fetchAPIWithLog(statsParquetUrl);
    return (
      typeof resp === 'string' ? JSON.parse(resp) : resp
    ) as IntradayStats;
  };

  const fetchStats = async (retries = 0) => {
    const dateKey = getDateFormatted(intradayDate);
    if (statsDataMap.has(dateKey)) {
      return;
    }

    try {
      const json = await fetchJson();
      nonProdDebugLog('fetched stats data', json);
      setStatsDataMap((map) => new Map(map.set(dateKey, json)));
    } catch (err) {
      console.error(err);
      if (retries < 1) {
        setTimeout(() => fetchStats(retries + 1), 5_000);
      } else {
        openToast({
          type: 'error',
          message: `There was an error loading stats data.`,
        });
      }
      return;
    }
  };

  useEffect(() => {
    fetchStats();
  }, [statsParquetUrl, statsDataMap, intradayDate, userTraceV2Enabled]);

  return { statsDataMap };
};
